import dayjs from "dayjs";

/**
 * Get the date an order needs to be picked, given its delivery date and the
 * lead time (in days) configured for the customer's country + delivery label.
 * @param {Object} deliveryDate - dayjs date object
 * @param {number} leadDays - number of days between picking and delivery
 * @returns {Object} dayjs date object, start of day
 */
export const getPickingDate = (deliveryDate, leadDays) => {
  return dayjs(deliveryDate).subtract(leadDays ?? 1, "day").startOf("day");
};

/**
 * Validate if a delivery day matches customer and delivery label settings
 * @param {Object} date - dayjs date object
 * @param {Object} customer - customer object with customerDeliveryDays
 * @param {string} label - delivery label code
 * @param {Function} getCountryDelivery - function to get country delivery settings
 * @param {string} COMPANY_COUNTRY_CODE - default country code
 * @param {Function} t - translation function
 * @returns {string|null} - warning message or null if valid
 */
export const validateDeliveryDay = (date, customer, label, getCountryDelivery, COMPANY_COUNTRY_CODE, t) => {
  if (!date || !customer || !label) {
    return null;
  }

  const dayOfWeek = dayjs(date).day(); // 0 = Sunday, 1 = Monday, etc.
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = dayNames[dayOfWeek];

  // Get delivery label settings from countries config
  const countryDeliverySettings = getCountryDelivery(customer.country || COMPANY_COUNTRY_CODE);
  
  const labelConfig = countryDeliverySettings?.deliveryLabels?.find((dl) => dl.deliveryLabel === label);
  
  const labelDeliveryDays = labelConfig?.deliveryDays || [];

  // Get customer delivery days
  const customerDeliveryDays = customer.customerDeliveryDays
    ? customer.customerDeliveryDays.split(",").map(Number)
    : [];

  // Check if day matches label settings
  const matchesLabel = labelDeliveryDays.length === 0 || labelDeliveryDays.includes(dayOfWeek);

  // Check if day matches customer settings
  const matchesCustomer = customerDeliveryDays.length === 0 || customerDeliveryDays.includes(dayOfWeek);

  // Return appropriate warning message with day name
  if (!matchesLabel && !matchesCustomer) {
    return `${t("delivery_day_mismatch_both").replace("The selected delivery day", `The selected delivery day (${dayName})`)}`;
  } else if (!matchesLabel) {
    return `${t("delivery_day_mismatch_label").replace("The selected delivery day", `The selected delivery day (${dayName})`)}`;
  } else if (!matchesCustomer) {
    return `${t("delivery_day_mismatch_customer").replace("The selected delivery day", `The selected delivery day (${dayName})`)}`;
  }

  return null;
};
